/**
 * Shared utilities for JSON processing between HandleJSONUpload and HandleJSONValidation
 */

// Type definitions for better type safety
export interface RowData {
  [key: string]: string | number | null | undefined;
}

export interface TransformedRowData {
  [key: string]: string | number | null;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string | null;
  count?: number;
}

export interface ApiConfig {
  apiEndpoint: string | undefined;
  fileTypeLabel: string;
}

export interface EnvironmentInterface {
  getJsonUploadIN02API: () => string;
  getJsonValidationIN02API: () => string;
  getJsonUploadAPI: () => string;
  getJsonValidationAPI: () => string;
  getJsonUploadCustomIN02API: () => string;
  getJsonValidationCustomIN02API: () => string;
  getJsonUploadIN02CombinedAPI: () => string;
  getJsonValidationIN02CombinedAPI: () => string;
  getCustomIN02DCList: () => string[];
  getCombinedIN02DCList: () => string[];
}

// Field mapping from frontend display names to backend LoadGroupBean property names
export const FIELD_MAPPING: Record<string, string> = {
  'Facility': 'facilityId',
  'Route': 'route',
  'Shipment ID': 'shipmentId',
  'Stop #': 'numberOfStop',
  'Shipment Dispatch Date': 'dispatchDate',
  'Stop Destination Facility': 'stopDestinationFacility',
  'Wave Group': 'loadGroup',
  'Trailer Request Sent': 'trailerRequestSent',
  'Planned Door': 'plannedDoor',
  'Secondary Planned Door': 'secondaryPlannedDoor',
  'Tertiary Planned Door': 'tertiaryPlannedDoor',
  'Primary Staging Location': 'outboundstagingLocationPrimary',
  'Secondary Staging Location': 'secondaryOutboundStagingLocation',
  'Tertiary Staging Location': 'tertiaryStagingLocation',
  'Load Group FRZ': 'loadGroupFrz',
  'Load Group GRO': 'loadGroupGro',
  'Load Group PER': 'loadGroupPer',
  'Load Group GML': 'loadGroupGml',
  'Load Group PRO': 'loadGroupPro'
};

/**
 * Transforms and filters rows for JSON processing
 * @param rows - Raw rows from the frontend
 * @param tabType - The tab type (IN02, XDOCK, etc.) for conditional processing
 * @returns Filtered and transformed rows ready for backend processing
 */
export const transformAndFilterRows = (rows: RowData[], tabType?: string): TransformedRowData[] => {
  return rows
    .map(({ tempKey, ...rest }) => {
      const converted = Object.fromEntries(
        Object.entries(rest).map(([key, value]) => [key, value === '' ? null : value])
      );
      return converted;
    })
    .filter(row => Object.values(row).some(value => value !== null))
    .map(row => {
      // Transform field names from frontend format to backend property names
      const transformedRow: TransformedRowData = {};
      Object.entries(row).forEach(([key, value]) => {
        const backendKey = FIELD_MAPPING[key] || key;
        let processedValue = value === undefined ? null : value;
        
        // Convert Facility values to uppercase for IN02 tab
        if (tabType === 'IN02' && key === 'Facility' && processedValue && typeof processedValue === 'string') {
          processedValue = processedValue.toUpperCase();
        }
        
        transformedRow[backendKey] = processedValue;
      });
      return transformedRow;
    });
};

/**
 * Validates that IN02 rows have at least one Load Group field populated
 * @param filteredRows - Transformed rows to validate
 * @param selectedDC - Selected distribution center
 * @param Environment - Environment utility object
 * @returns Object with isValid boolean and error message if invalid
 */
export const validateIN02LoadGroups = (
  filteredRows: TransformedRowData[], 
  selectedDC: string,
  Environment: EnvironmentInterface
): ValidationResult => {
  const loadGroupFields = ['loadGroupFrz', 'loadGroupGro', 'loadGroupPer', 'loadGroupGml', 'loadGroupPro'];
  const loadGroupFieldNames = ['FRZ', 'GRO', 'PER', 'GML', 'PRO'];
  
  // Check if this is a Custom IN02 DC
  const customIN02DCs = Environment.getCustomIN02DCList();
  const isCustomIN02DC = customIN02DCs.includes(selectedDC);

  // Check if this is a Combined IN02 DC (e.g. WAUB)
  const combinedIN02DCs = Environment.getCombinedIN02DCList();
  const isCombinedIN02DC = combinedIN02DCs.includes(selectedDC);
  
  // Helper function to validate load group value based on DC type
  const isValidLoadGroupValue = (value: any): boolean => {
    if (!value && value !== 0) return true; // Empty values are allowed
    
    const stringValue = String(value).trim();
    if (stringValue === '') return true; // Empty string is allowed
    
    if (isCustomIN02DC) {
      // Custom IN02: Allow any numerical value > 0
      if (!/^[0-9]+$/.test(stringValue)) return false; // Must contain only digits
      const numValue = parseInt(stringValue, 10);
      return numValue > 0;
    } else if (isCombinedIN02DC) {
      // Combined IN02 (e.g. WAUB): Allow any numerical value > 0
      if (!/^[0-9]+$/.test(stringValue)) return false; // Must contain only digits
      const numValue = parseInt(stringValue, 10);
      return numValue > 0;
    } else {
      // Normal IN02: Must be 1 or 2 digits (1-99)
      if (!/^\d{1,2}$/.test(stringValue)) return false;
      
      // Parse and validate range: must be between 1-99
      const numValue = parseInt(stringValue, 10);
      return numValue > 0 && numValue <= 99;
    }
  };
  
  for (let i = 0; i < filteredRows.length; i++) {
    const row = filteredRows[i];
    let hasAtLeastOneLoadGroup = false;
    
    // Check each load group field
    for (let j = 0; j < loadGroupFields.length; j++) {
      const field = loadGroupFields[j];
      const fieldName = loadGroupFieldNames[j];
      const value = row[field];
      
      // Check if field has a value
      const hasValue = value && value !== null && 
                      (typeof value === 'string' ? value.trim() !== '' : 
                       typeof value === 'number' ? true : 
                       String(value).trim() !== '');
      
      if (hasValue) {
        hasAtLeastOneLoadGroup = true;
        
        // Validate the format of the value
        if (!isValidLoadGroupValue(value)) {
          const errorMsg = (isCustomIN02DC || isCombinedIN02DC)
            ? `Error in line ${i + 1} - Load Group ${fieldName} must contain only numerical values greater than 0. Invalid value: "${value}"`
            : `Error in line ${i + 1} - Load Group ${fieldName} must be a number from 1 to 99. Invalid value: "${value}"`;
          return {
            isValid: false,
            errorMessage: errorMsg,
            count: 0
          };
        }
      }
    }
    
    // Check if at least one load group has a value
    if (!hasAtLeastOneLoadGroup) {
      return {
        isValid: false,
        errorMessage: `Error in line ${i + 1} - At least one Load Group field (FRZ, GRO, PER, GML, PRO) must have a value`,
        count: 0
      };
    }
  }
  
  return { 
    isValid: true, 
    errorMessage: null, 
    count: filteredRows.length 
  };
};

/**
 * Prepares FormData for API submission
 * @param filteredRows - Transformed and validated rows
 * @param selectedDC - Selected distribution center
 * @returns FormData object ready for API submission
 */
export const prepareFormData = (filteredRows: TransformedRowData[], selectedDC: string): FormData => {
  const data = new FormData();
  data.append('data', JSON.stringify(filteredRows));
  data.append('dcName', selectedDC);
  return data;
};

/**
 * Gets the appropriate API endpoint and file type label based on tab type and selected DC
 * @param tabType - The tab type (IN02, XDOCK, etc.)
 * @param apiType - The type of API ('upload' or 'validation')
 * @param Environment - Environment utility object
 * @param selectedDC - The selected distribution center
 * @returns Object with API endpoint and file type label
 */
export const getApiConfig = (
  tabType: string, 
  apiType: 'upload' | 'validation',
  Environment: EnvironmentInterface,
  selectedDC?: string
): ApiConfig => {
  let apiEndpoint: string | undefined;
  let fileTypeLabel: string;
  
  console.log('DEBUG - getApiConfig called:', { tabType, apiType, selectedDC });
  
  switch (tabType) {
    case 'IN02':
      // Check combined IN02 DC first (e.g. WAUB)
      const combinedIN02DCs = Environment.getCombinedIN02DCList();
      const isCombinedIN02DC = selectedDC && combinedIN02DCs.includes(selectedDC);

      console.log('DEBUG - IN02 DC check:', { combinedIN02DCs, selectedDC, isCombinedIN02DC });

      // Check if the selected DC is in the custom IN02 DC list
      const customIN02DCs = Environment.getCustomIN02DCList();
      const isCustomIN02DC = selectedDC && customIN02DCs.includes(selectedDC);

      console.log('DEBUG - Custom IN02 DC check:', { customIN02DCs, selectedDC, isCustomIN02DC });
      
      if (isCombinedIN02DC) {
        apiEndpoint = apiType === 'upload'
          ? Environment.getJsonUploadIN02CombinedAPI()
          : Environment.getJsonValidationIN02CombinedAPI();
        fileTypeLabel = 'IN02';
        console.log('DEBUG - Using Combined API:', { apiEndpoint, fileTypeLabel });
      } else if (isCustomIN02DC) {
        apiEndpoint = apiType === 'upload' 
          ? Environment.getJsonUploadCustomIN02API()
          : Environment.getJsonValidationCustomIN02API();
        fileTypeLabel = 'Custom IN02';
        console.log('DEBUG - Using Custom API:', { apiEndpoint, fileTypeLabel });
      } else {
        apiEndpoint = apiType === 'upload' 
          ? Environment.getJsonUploadIN02API()
          : Environment.getJsonValidationIN02API();
        fileTypeLabel = 'IN02';
        console.log('DEBUG - Using Regular IN02 API:', { apiEndpoint, fileTypeLabel });
      }
      break;
    case 'XDOCK':
    default:
      apiEndpoint = apiType === 'upload'
        ? Environment.getJsonUploadAPI()
        : Environment.getJsonValidationAPI();
      fileTypeLabel = 'XDOCK';
      console.log('DEBUG - Using XDOCK API:', { apiEndpoint, fileTypeLabel });
      break;
  }
  
  return { apiEndpoint, fileTypeLabel };
};
